import DiscoveredCreatorCard from 'Components/creator-discovery/DiscoveredCreatorCard';
import DiscoveryResultsSummary from 'Components/creator-discovery/DiscoveryResultsSummary';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import {
    DISCOVERY_CREATOR_SORT_LABELS,
    DiscoveredCreator,
    DiscoveryCreatorSort,
} from 'types/creator-discovery';

interface DiscoveredCreatorsResultsProps {
    title?: string;
    creators: DiscoveredCreator[];
    count: number;
    loading: boolean;
    sort: DiscoveryCreatorSort;
    selectedIds: string[];
    query?: string;
    showSummary?: boolean;
    scrollableTarget: string;
    downloading?: boolean;
    inviting?: boolean;
    inviteLabel?: string;
    inviteDisabledTooltip?: string;
    emptyTitle?: string;
    emptyText?: string;
    className?: string;
    onSortToggle: () => void;
    onToggleSelect: (creatorId: string) => void;
    onClearSelection: () => void;
    onLoadMore: () => void;
    onDownload: () => void;
    onInvite: () => void;
}

const DiscoveredCreatorsResults = ({
    title = 'Results',
    creators,
    count,
    loading,
    sort,
    selectedIds,
    query,
    showSummary = true,
    scrollableTarget,
    downloading = false,
    inviting = false,
    inviteLabel = 'Invite to Campaign',
    inviteDisabledTooltip,
    emptyTitle = 'No creators found',
    emptyText = "We couldn't find creators matching this query. Try refining your search with more specific criteria.",
    className,
    onSortToggle,
    onToggleSelect,
    onClearSelection,
    onLoadMore,
    onDownload,
    onInvite,
}: DiscoveredCreatorsResultsProps) => {
    const hasSelection = selectedIds.length > 0;
    const showGrid = creators.length > 0;
    const showEmpty = !loading && creators.length === 0;
    const showInitialSkeleton = loading && creators.length === 0;
    const defaultInviteTooltip =
        count === 0
            ? 'No creators found yet.'
            : 'Select at least one creator to invite them to a campaign.';

    return (
        <div className={`creator-discovery-results${className ? ` ${className}` : ''}`}>
            <div className="creator-discovery-results__header">
                <div className="creator-discovery-results__header-left">
                    <h2 className="creator-discovery-results__title">{title}</h2>
                    {count > 0 && (
                        <span className="creator-discovery-results__count">{count} creators</span>
                    )}
                    {hasSelection && (
                        <>
                            <span className="creator-discovery-results__selected-count">
                                {selectedIds.length} selected
                            </span>
                            <button
                                type="button"
                                className="creator-discovery-results__clear"
                                onClick={onClearSelection}
                            >
                                Clear
                            </button>
                        </>
                    )}
                </div>
                <div className="creator-discovery-results__header-actions">
                    <span id="discovered-creators-invite-btn" className="d-inline-block">
                        <button
                            type="button"
                            disabled={!hasSelection || inviting}
                            className="btn btn-primary creator-discovery-results__action-btn"
                            onClick={onInvite}
                            style={{ pointerEvents: hasSelection ? 'auto' : 'none' }}
                        >
                            {inviting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" />
                                    Inviting...
                                </>
                            ) : (
                                <>
                                    <i className="ri-mail-send-line" />
                                    {inviteLabel}
                                </>
                            )}
                        </button>
                    </span>
                    {!hasSelection && (
                        <UncontrolledTooltip
                            placement="top"
                            target="discovered-creators-invite-btn"
                            fade={false}
                            autohide={false}
                        >
                            {inviteDisabledTooltip ?? defaultInviteTooltip}
                        </UncontrolledTooltip>
                    )}
                    {/* <button
                        type="button"
                        className="creator-discovery-results__download-btn"
                        onClick={onDownload}
                        disabled={downloading || count === 0}
                    >
                        <i className="ri-download-2-line" />
                        <span>{downloading ? 'Downloading...' : 'Download'}</span>
                    </button> */}
                    {!showSummary && (
                        <button
                            type="button"
                            className="creator-discovery-results__sort-btn"
                            onClick={onSortToggle}
                        >
                            <i className="ri-arrow-up-down-line" />
                            Sort: {DISCOVERY_CREATOR_SORT_LABELS[sort]}
                        </button>
                    )}
                </div>
            </div>

            <div id={scrollableTarget} className="creator-discovery-results__body">
                {showSummary && query && (
                    <DiscoveryResultsSummary query={query} sort={sort} onSortToggle={onSortToggle} />
                )}

                {showInitialSkeleton && (
                    <div className="discovered-creators-list">
                        {Array.from({ length: 8 }).map((_, index) => (
                            <Skeleton key={index} height={88} className="rounded-3" />
                        ))}
                    </div>
                )}

                {showGrid && (
                    <InfiniteScroll
                        dataLength={creators.length}
                        next={onLoadMore}
                        hasMore={creators.length < count}
                        scrollableTarget={scrollableTarget}
                        loader={
                            <div className="creator-discovery-load-more">
                                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                            </div>
                        }
                    >
                        <div className="discovered-creators-list">
                            {creators.map((creator) => (
                                <DiscoveredCreatorCard
                                    key={creator.uuid}
                                    creator={creator}
                                    isSelected={selectedIds.includes(creator.uuid)}
                                    onToggleSelect={onToggleSelect}
                                />
                            ))}
                        </div>
                    </InfiniteScroll>
                )}

                {showEmpty && (
                    <div className="creator-discovery-empty">
                        <div className="creator-discovery-empty__icon">
                            <i className="ri-user-unfollow-line" />
                        </div>
                        <h3 className="creator-discovery-empty__title">{emptyTitle}</h3>
                        <p className="creator-discovery-empty__text">{emptyText}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DiscoveredCreatorsResults;
